/**
 * Converts an interpolated value (which may contain rich-text HTML or entities
 * like `&nbsp;`) into **plain text**. Used by `interpolate(...).text` when the
 * caller opts in via `options.sanitizeHtml` — e.g. for SEO/Social meta tags
 * (`<title>`, `meta description`, `og:title`, `og:description`).
 *
 * DOM-free implementation (runs on SSR/Node) and intentionally kept in sync
 * with the admin util (atd-workspace-ui `src/common/utils/cms/sanitizeSeoText.ts`)
 * so the editor preview matches the published HTML. See ATW-230 (CT-11/14/15):
 * rich-text variables came out as `<p>...</p>` and price as `R$&nbsp;...`.
 *
 * IMPORTANT: any change to this algorithm must be mirrored in the admin util.
 */

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  // pt-BR accents (lower and upper case)
  aacute: 'á', eacute: 'é', iacute: 'í', oacute: 'ó', uacute: 'ú',
  Aacute: 'Á', Eacute: 'É', Iacute: 'Í', Oacute: 'Ó', Uacute: 'Ú',
  agrave: 'à', Agrave: 'À', egrave: 'è', igrave: 'ì', ograve: 'ò', ugrave: 'ù',
  acirc: 'â', ecirc: 'ê', ocirc: 'ô', Acirc: 'Â', Ecirc: 'Ê', Ocirc: 'Ô',
  atilde: 'ã', otilde: 'õ', Atilde: 'Ã', Otilde: 'Õ',
  ccedil: 'ç', Ccedil: 'Ç',
  uuml: 'ü', Uuml: 'Ü', ntilde: 'ñ', Ntilde: 'Ñ',
  ordf: 'ª', ordm: 'º', deg: '°', middot: '·', bull: '•',
  hellip: '…', mdash: '—', ndash: '–', laquo: '«', raquo: '»',
  copy: '©', reg: '®', trade: '™',
  lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
  // Common symbols in prices/descriptions
  euro: '€', pound: '£', cent: '¢', yen: '¥', curren: '¤',
  sect: '§', para: '¶', dagger: '†', permil: '‰',
  times: '×', divide: '÷', plusmn: '±', micro: 'µ',
  frac12: '½', frac14: '¼', frac34: '¾', sup2: '²', sup3: '³', sup1: '¹',
  iexcl: '¡', iquest: '¿',
};

const toCodePoint = (value: number, fallback: string): string => {
  try {
    return String.fromCodePoint(value);
  } catch {
    // Invalid/out-of-range codepoint (RFC): keep the original text.
    return fallback;
  }
};

const decodeEntities = (input: string): string =>
  input
    .replace(/&#x([0-9a-fA-F]+);/g, (match, hex) =>
      toCodePoint(parseInt(hex, 16), match),
    )
    .replace(/&#(\d+);/g, (match, dec) => toCodePoint(parseInt(dec, 10), match))
    .replace(/&([a-zA-Z][a-zA-Z0-9]*);/g, (match, name) =>
      Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, name)
        ? NAMED_ENTITIES[name]
        : match,
    );

export const sanitizeToPlainText = (value: string): string => {
  if (!value) return '';

  // Tag → space (avoids gluing words from adjacent block/inline elements).
  const withoutTags = value.replace(/<[^>]*>/g, ' ');
  const decoded = decodeEntities(withoutTags);

  // `\s` in JS includes U+00A0 (decoded nbsp); normalize to a regular space and
  // collapse line breaks / multiple spaces coming from rich text.
  return decoded.replace(/\s+/g, ' ').trim();
};
